import {
    Directive,
    forwardRef,
    HostListener,
    inject,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges
} from "@angular/core";
import {
    AbstractControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from "@angular/forms";
import {
    ILgFormatter,
    ILgFormatterOptions,
    LgFormatterFactoryService
} from "@logex/framework/core";

import { ValueAccessorBase } from "../value-accessor-base";

@Directive({
    selector: "input[lgFormatter][ngModel], input[lgFormatter][formControlName]",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LgFormatterDirective),
            multi: true
        },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => LgFormatterDirective), multi: true }
    ]
})
export class LgFormatterDirective
    extends ValueAccessorBase<number | null>
    implements Validator, OnInit, OnChanges
{
    private _formatterFactory = inject(LgFormatterFactoryService);

    /**
     * Formatter type.
     *
     * @default "NONE"
     */
    @Input("lgFormatter") type = "NONE";

    /**
     * Formatter options definition.
     */
    @Input("lgFormatterOptions") options?: ILgFormatterOptions;

    private _formatterInstance?: ILgFormatter<any>;
    private _focused = false;
    // undefined = not calculated yet. The form directive can run its first validation
    // before our ngOnInit/ngOnChanges, so validate() must be able to detect that.
    private _valid: boolean | undefined;
    private _onValidatorChange?: () => void;

    constructor() {
        super();
    }

    ngOnInit(): void {
        this._focused = false;

        this._initializeFormatter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        let isFormattingRequired = false;

        if ("type" in changes && changes.type.currentValue !== changes.type.previousValue) {
            this._initializeFormatter();
            isFormattingRequired = true;
        } else if ("options" in changes) {
            // Options take part in parsing, so the cached verdict no longer applies.
            this._invalidateValidity();
            isFormattingRequired = true;
        }

        if (isFormattingRequired) {
            if (this._focused) {
                this._formatForEditing();
            } else {
                this._formatValue();
            }
        }
    }

    validate(c: AbstractControl): ValidationErrors | null {
        if (this._valid === undefined) {
            // First run: nothing has parsed the value yet, so ask the formatter about it.
            this._valid = this._isValueValid(c.value);
        }

        return this._valid ? null : { lgFormatter: { valid: false } };
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    @HostListener("input", ["$any($event.target).value"])
    public _input(value: string): void {
        if (value != null && value !== "") {
            const parseResult = this._formatter.parse(value, this.options);
            this._valid = parseResult.isValid;
            this.value = parseResult.result;
        } else {
            this._valid = true;
            this.value = null;
        }
    }

    @HostListener("blur")
    public _blur(): void {
        this._focused = false;
        this._formatValue();
    }

    @HostListener("focus")
    public _focus(): void {
        this._focused = true;
        this._formatForEditing();
    }

    writeValue(value: number): void {
        if (value == null) {
            this._renderer.setProperty(this._elementRef.nativeElement, "value", "");
        } else {
            const elemValue = this._focused
                ? this._formatter.formatForEditing(value, this.options)
                : this._formatter.format(value, this.options);
            this._renderer.setProperty(this._elementRef.nativeElement, "value", elemValue);
        }
        // The value comes from the outside (including lgUndoOnEsc restoring the last valid
        // value), so the previously typed value's validity no longer applies.
        this._valid = this._isValueValid(value);

        this._writeValue(value);
    }

    setDisabledState(isDisabled: boolean): void {
        this._renderer.setProperty(this._elementRef.nativeElement, "disabled", isDisabled);
    }

    private get _formatter(): ILgFormatter<any> {
        // Created lazily: the form directive may call writeValue()/validate() before ngOnInit.
        if (this._formatterInstance === undefined) {
            this._initializeFormatter();
        }

        return this._formatterInstance!;
    }

    private _initializeFormatter(): void {
        const isReplacement = this._formatterInstance !== undefined;
        this._formatterInstance = this._formatterFactory.getFormatter(this.type, this.options);

        if (isReplacement) {
            this._invalidateValidity();
        }
    }

    /**
     * Drops a verdict that was reached with a different formatter and asks the form to
     * validate again. No-op while nothing has been validated yet.
     */
    private _invalidateValidity(): void {
        if (this._valid === undefined) {
            return;
        }

        this._valid = undefined;
        this._onValidatorChange?.();
    }

    private _isValueValid(value: unknown): boolean {
        if (value == null || value === "") {
            return true;
        }

        // parse() consumes the text the user typed, so only a string can be an unparsed
        // value - anything else already is the formatter's own value type. Stringifying it
        // would be wrong: "" + 1e-7 is "1e-7", which no numeric formatter accepts.
        if (typeof value !== "string") {
            return !Number.isNaN(value);
        }

        return this._formatter.parse(value, this.options).isValid;
    }

    private _formatValue(): void {
        if ((this.value as any) === "") {
            // When bluring an empty field that was never edited, we want to keep it empty
            this._renderer.setProperty(this._elementRef.nativeElement, "value", "");
        } else if (this._valid !== false && this.value != null) {
            const formatted = this._formatter.format(this.value, this.options);
            this._renderer.setProperty(this._elementRef.nativeElement, "value", formatted);
        }
        this.touch();
    }

    private _formatForEditing(): void {
        if (this._valid !== false) {
            const formatted = this._formatter.formatForEditing(this.value);

            if (formatted !== this._elementRef.nativeElement.value) {
                this._renderer.setProperty(this._elementRef.nativeElement, "value", formatted);
            }
        }
    }
}
